import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpService } from '../shared/services/http.service';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ControllerService } from '../shared/services/controller.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NewReportModalComponent } from './new-report-modal/new-report-modal.component';

import { MessageModalComponent } from '../shared/message-modal/message-modal.component';
import { MAT_DATE_LOCALE } from '@angular/material/core';

@Component({
    selector: 'app-adhoc-report',
    templateUrl: './adhoc-report.component.html',
    styleUrls: ['./adhoc-report.component.scss'],
    providers: [{ provide: MAT_DATE_LOCALE, useValue: 'en-GB' }],
    standalone: false
})
export class AdhocReportComponent implements OnInit {

    data = []
    dataSource: any;
    @ViewChild(MatSort, { static: true }) sort: MatSort = new MatSort();

    submitted = false

    displayedColumns = []

    reports = []
    selectedReportId: any;

    searchForm: any

    columnNames = [];

    constructor(private controller: ControllerService, private httpService: HttpService, private fb: UntypedFormBuilder, private route: ActivatedRoute, private modalService: NgbModal
    ) { }

    async newReport() {
        try {
            const modalRef = this.modalService.open(NewReportModalComponent, {
                size: 'xl',
                windowClass: 'center-modal-window extra-wide-modal'
            });

            const result = await modalRef.result;

            if (result) {
                const res: any = await this.httpService.postAsync('report/saveReport', result);
                if (res.success) {
                    await this.fetchReports();
                } else {
                    const modalRef = this.modalService.open(MessageModalComponent);
                    modalRef.componentInstance.title = "Error";
                    modalRef.componentInstance.message = "Error creating report: " + res.message;
                }
            }
        } catch (e) {
            // dismissed
        }
    }

    async editReport() {
        const reportId = this.searchForm.get('reportId').value;
        if (!reportId) return;

        const report = this.reports.find(r => r.id == reportId);
        if (!report) return;

        try {
            const modalRef = this.modalService.open(NewReportModalComponent, {
                size: 'xl',
                windowClass: 'center-modal-window extra-wide-modal'
            });

            modalRef.componentInstance.report = report;

            const result = await modalRef.result;

            if (result) {
                const payload = { ...result, id: reportId };
                const res: any = await this.httpService.postAsync('report/saveReport', payload);
                if (res.success) {
                    await this.fetchReports();
                    // Optionally re-select the report or refresh data if needed
                } else {
                    const modalRef = this.modalService.open(MessageModalComponent);
                    modalRef.componentInstance.title = "Error";
                    modalRef.componentInstance.message = "Error updating report: " + res.message;
                }
            }
        } catch (e) {
            // dismissed
        }
    }


    createSearchForm(fromDate) {
        this.searchForm = this.fb.group({
            reportId: [],
            fromDate: [fromDate],
            toDate: [],
        })
    }

    get f() {
        return this.searchForm.controls
    }


    async onSubmit() {
        const formModel = this.searchForm.value;

        this.submitted = true
        if (this.searchForm.invalid) {
            return
        }

        if (!formModel.reportId) {
            return;
        }

        let fromDate = formModel.fromDate;
        let toDate = formModel.toDate;

        if (fromDate instanceof Date) {
            fromDate = fromDate.toISOString().split('T')[0];
        }
        if (toDate instanceof Date) {
            toDate = toDate.toISOString().split('T')[0];
        }

        await this.fetchData(formModel.reportId, fromDate, toDate)

        this.submitted = false
    }

    skeletonRows: number[][] = [];
    loading = false;

    generateSkeletonRows() {
        this.skeletonRows = Array(10).fill(0).map(() => {
            // Random number of bars between 5 and 8 to ensure density
            const barCount = Math.floor(Math.random() * (8 - 5 + 1)) + 5;
            const bars = [];
            for (let i = 0; i < barCount; i++) {
                // Random flex weight between 1 and 10 to give variety in width
                bars.push(Math.floor(Math.random() * 10) + 1);
            }
            return bars;
        });
    }

    async fetchData(reportId, fromDate, toDate) {

        this.data = []
        if (this.dataSource) {
            this.dataSource.data = []
        }

        this.loading = true;
        this.generateSkeletonRows();


        try {
            const response: any = await this.httpService.postAsync('report/runReport', { reportId, fromDate, toDate })

            if (!response.success) {
                const modalRef = this.modalService.open(MessageModalComponent);
                modalRef.componentInstance.title = "Error";
                modalRef.componentInstance.message = response.message || "Error communicating with server.";
                return
            }
            this.data = response.data

            // Dynamically determine columns from the first record if available
            if (this.data.length > 0) {
                const firstRow = this.data[0];
                this.displayedColumns = Object.keys(firstRow);
                this.columnNames = this.displayedColumns.map(col => ({ id: col, value: this.formatColumnName(col) }));
            } else {
                this.displayedColumns = [];
                this.columnNames = [];
            }

            this.dataSource = new MatTableDataSource(this.data);
            this.dataSource.sort = this.sort;

        } finally {
            this.loading = false
        }

    }

    formatColumnName(key: string): string {
        // Simple formatter: replace underscores with spaces and capitalize
        return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }

    async downloadExcel() {
        const formModel = this.searchForm.value;
        const reportId = formModel.reportId;
        const fromDate = formModel.fromDate;
        const toDate = formModel.toDate;

        if (!reportId) {
            alert("Please select a report.");
            return;
        }

        // Find report name for filename
        const report = this.reports.find(r => r.id == reportId);
        const reportName = report ? report.name : 'report';


        let qs = `?reportId=${reportId}`
        if (fromDate) qs += `&fromDate=${fromDate}`
        if (toDate) qs += `&toDate=${toDate}`


        try {
            const blob: any = await this.httpService.getBlob('report/excelReport' + qs)

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${reportName}-${fromDate || 'all'}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } finally {

        }
        return false
    }

    async fetchReports() {
        const res: any = await this.httpService.getAsync("report/getReports")
        if (res.success) {
            this.reports = res.data;
        }
    }


    async ngOnInit() {

        const date = new Date();
        // Create the 1st of the current month
        const firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);

        // Format to YYYY-MM-DD using local values
        const firstOfMonthString = [
            firstOfMonth.getFullYear(),
            String(firstOfMonth.getMonth() + 1).padStart(2, '0'),
            '01'
        ].join('-');

        this.createSearchForm(firstOfMonthString)

        await this.fetchReports()
        await this.fetchAcademicYears()

        this.dataSource = new MatTableDataSource(this.data);
        this.dataSource.sort = this.sort;
    }

    academicYears = [];

    async fetchAcademicYears() {
        const res: any = await this.httpService.postAsync("report/academicYears", {})
        this.academicYears = res.academicYears
    }

    async selectAcademicYear(year) {
        if (!year) return;

        const ayear = this.academicYears.find(yearObject => yearObject.formatted_year == year)

        // set formControl values
        this.f.fromDate.setValue(ayear.start_date.date.toString().substring(0, 10))
        this.f.toDate.setValue(ayear.end_date.date.toString().substring(0, 10))

        // Trigger report run
        await this.onSubmit();
    }

}
